import { sampleGivenName, sampleSalutation, sampleSurname } from '../../../../lib/sampleNames';
import type { FamilyRelation } from '../../family-relations/familyRelationTypes';

/**
 * Test data backing the Autogenerate button on the family form.
 *
 * **The name is REAL-LOOKING, by product call on 31 Oct 2026** — `lib/sampleNames.ts` holds the
 * vocabulary and what the reversal gave up. It used to be deliberately not a name, so that a row
 * in `rsys_pii` could be told from a real person's by reading it; that protection is gone across
 * every personnel generator at once, because half of them reading as tokens and half as names
 * would be the worse of the two worlds.
 *
 * **It takes the CATALOG, and that is not optional.** The relation became a required field when
 * it stopped being an enum, so a generator that did not fill one would leave Save disabled —
 * which is precisely the silent breakage CLAUDE.md warns about, because nothing fails and the
 * button simply stops working. It also has to resolve against the rows the form actually
 * loaded: an id this function invented would fail the server's "it must exist and be active"
 * check, and that reads as a bug in the form.
 *
 * Reading the chosen row's flags is what keeps it honest against a relation an administrator
 * added: a generator that special-cased the four seeded names would fill nothing for "Guardian".
 *
 * Keep it in lockstep with the form: a new required field there without a value here silently
 * breaks the button, and nothing fails.
 */
export function sampleFamilyMember(
  /** The active relations the form loaded. Empty only while the fetch is in flight or failed. */
  relations: FamilyRelation[],
  /** What the reader has already picked, if anything. */
  chosen: FamilyRelation | null,
): {
  relationId: string;
  salutation: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  dateOfBirth: string;
  occupation: string;
  employerName: string;
  businessAddress: string;
  telephoneNumber: string;
} {

  /*
    The reader's own choice wins — "never a field the reader has already decided". Otherwise the
    FIRST active relation, which is the catalog's own order rather than a random pick: a
    generator whose output the reader cannot predict is worse than one that fills less.
  */
  const relation = chosen ?? relations[0] ?? null;

  /*
    Filled whenever the relation requires one, because the server refuses the save without it
    and the form marks the field required — a generator that left it blank would produce a form
    that cannot be submitted, which is the one thing Autogenerate exists to prevent.

    Dated plainly in the past: a window computed from `now` eventually drifts past the max the
    field allows.
  */
  const dateOfBirth = relation?.requiresDateOfBirth ? '2015-04-12' : '';

  /*
    The occupation block is drawn only where the relation asks for it. Filling those four for a
    relation that does not would put values on screen the form never shows, which is a worse
    test than leaving them empty.
  */
  const occupational = relation?.collectsOccupation ?? false;

  const sexKind: 'female' | 'male' = Math.random() < 0.5 ? 'female' : 'male';

  return {
    relationId: relation?.id ?? '',
    /*
      One sex decides the given name AND the title, because this row carries no sex field of its
      own and the salutation is therefore the only thing stating one. Drawn apart they contradict
      each other on the screen — `Mr. Maria` — which is the first thing a reader notices.
    */
    salutation: sampleSalutation(sexKind),
    firstName: sampleGivenName(sexKind),

    // A middle name here is the mother's MAIDEN SURNAME, so it comes off the surname list.
    middleName: sampleSurname(),
    lastName: sampleSurname(),
    suffix: '',
    dateOfBirth,
    occupation: occupational ? 'Placeholder occupation' : '',
    employerName: occupational ? 'Example Employer (test)' : '',
    businessAddress: occupational ? 'Sample Address, Testville' : '',

    // The reserved fiction block, so a generated number can never reach a real handset.
    telephoneNumber: occupational ? '+63 917 555 0142' : '',
  };
}
